import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import isHotkey from "is-hotkey";
import { useTranslation } from "react-i18next";
import {
  Editable,
  withReact,
  useSlate,
  Slate,
  ReactEditor,
  useSlateStatic,
  useReadOnly,
} from "slate-react";
import {
  Editor,
  Transforms,
  createEditor,
  Descendant,
  Element as SlateElement,
  BaseEditor,
  Point,
  Range,
} from "slate";
import "./styles.scss";

import DefaultButton, { ButtonProps } from "Components/Atoms/Button";
import Box from "Components/Atoms/Box";
import {
  CheckCircle,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  FormatUnderlined,
  LooksOne,
  LooksTwo,
} from "Components/Atoms/Icons";
import Checkbox from "Components/Atoms/Checkbox";
import Divider from "Components/Atoms/Divider";
import { uniqueId } from "lodash";
import { noop } from "utils/helper";

const Button: React.FC<
  ButtonProps & {
    active: boolean;
    icon: (p: { size: number; color: string }) => React.ReactNode;
  }
> = ({ active, icon, ...props }) => {
  return (
    <DefaultButton {...props} variant={active ? "outlined" : "default"}>
      {icon({ size: 16, color: "inherited" })}
    </DefaultButton>
  );
};

const HOTKEYS: Record<string, string> = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
  "mod+`": "code",
};

const LIST_TYPES = ["numbered-list", "bulleted-list"];

const createEmptyValue = (): Descendant[] => {
  const paragraph = { type: "paragraph", children: [{ text: "" }] };
  return [paragraph];
};

const sanitizeNode = (node: any): any => {
  if (node && "text" in node) {
    return typeof node.text === "string" ? node : { ...node, text: "" };
  }

  if (Array.isArray(node?.children) && node.children.length) {
    const children = node.children.map(sanitizeNode);
    const changed = children.some(
      (child: any, i: number) => child !== node.children[i]
    );
    return changed ? { ...node, children } : node;
  }

  return {
    ...node,
    type: node?.type ?? "paragraph",
    children: [{ text: "" }],
  };
};

const sanitizeValue = (value?: Descendant[]): Descendant[] => {
  if (!Array.isArray(value) || !value.length) return createEmptyValue();

  const nodes = value.map((node: any) => {
    if (node && "text" in node) {
      return { type: "paragraph", children: [sanitizeNode(node)] };
    }
    return sanitizeNode(node);
  });

  const changed = nodes.some((node, i) => node !== value[i]);

  return changed ? nodes : value;
};

const getLeaf = (nodes: Descendant[], path: number[]) => {
  let node: any = { children: nodes };

  for (const index of path) {
    node = node?.children?.[index];
    if (!node) return null;
  }

  return typeof node?.text === "string" ? node : null;
};

const isValidPoint = (nodes: Descendant[], point: Point) => {
  const leaf = getLeaf(nodes, point.path);
  return !!leaf && point.offset <= leaf.text.length;
};

const selectionFitsValue = (nodes: Descendant[], selection: Range | null) =>
  !selection ||
  (isValidPoint(nodes, selection.anchor) &&
    isValidPoint(nodes, selection.focus));

type Props = {
  value: Descendant[];
  onChange: (value: Descendant[]) => void;
  onBlur?: (value: Descendant[]) => void;
  placeholder?: string;
  disabled?: boolean;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  autoFocus?: boolean;
  style?: React.CSSProperties;
  onlyLimitedValues?: boolean;
  reload?: any;
  onClick?: () => void;
  extraButtons?: {
    icon: (p: { size: number; color: string }) => React.ReactNode;
    onClick: () => void;
    active?: boolean;
    title?: string;
  }[];
};

const TextEditor: React.FC<Props> = ({
  value,
  onChange,
  onBlur,
  placeholder = "Enter some text here...",
  disabled = false,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
  autoFocus,
  style,
  onlyLimitedValues = false,
  reload,
  onClick = noop,
  extraButtons,
}) => {
  const { t } = useTranslation("common");
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const blurHandler = useRef(onBlur);
  blurHandler.current = onBlur;

  useEffect(() => {
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, []);

  const renderElement = useCallback((props) => <Element {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
  const editor = useMemo(
    () => withReact(createEditor() as ReactEditor),
    [reload]
  );

  const [id] = useState(uniqueId());

  const [internalValue, setinternalValue] = useState(() =>
    sanitizeValue(value)
  );

  useEffect(() => {
    try {
      const next = sanitizeValue(value);

      if (!selectionFitsValue(next, editor.selection)) {
        // Clear the stale selection by raw assignment, not Transforms.deselect:
        // a deselect operation fires onChange with the editor's old children and
        // would push stale content back over an incoming (e.g. remote) value.
        editor.selection = null;
      }

      setinternalValue(next);
    } catch (error) {
      console.warn("TextEditor sanitizeValue failed (incoming value)", error);
    }
  }, [value]);

  const handleChange = (value: Descendant[]) => {
    if (disabled) return;
    try {
      let val = sanitizeValue(value);
      setinternalValue(val);
      onChange(val);
      if (timeout.current) clearTimeout(timeout.current);
      timeout.current = setTimeout(() => blurHandler.current?.(val), 1000);
    } catch (error) {
      console.warn("TextEditor sanitizeValue failed (onChange)", error);
    }
  };

  return (
    <Box id={id} className="slate-texteditor" style={style}>
      <Slate editor={editor} value={internalValue} onChange={handleChange}>
        <Box
          flex
          className="toolbar"
          padding="sm"
          role="group"
          aria-label={t("Text formatting")}
        >
          <Box
            style={{
              display: "grid",
              gap: 4,
              gridTemplateColumns: "46px 46px 46px",
              width: "auto",
              marginRight: 8,
            }}
          >
            <MarkButton
              disabled={disabled}
              format="bold"
              icon={(p: any) => <FormatBold {...p} />}
            />
            <MarkButton
              disabled={disabled}
              format="italic"
              icon={(p: any) => <FormatItalic {...p} />}
            />
            <MarkButton
              disabled={disabled}
              format="underline"
              icon={(p: any) => <FormatUnderlined {...p} />}
            />
          </Box>
          {!onlyLimitedValues && (
            <>
              <Box
                style={{
                  display: "grid",
                  gap: 4,
                  gridTemplateColumns: "46px 46px",
                  width: "auto",
                  marginRight: 8,
                }}
              >
                <BlockButton
                  disabled={disabled}
                  format="heading-one"
                  icon={(p: any) => <LooksOne {...p} />}
                />
                <BlockButton
                  disabled={disabled}
                  format="heading-two"
                  icon={(p: any) => <LooksTwo {...p} />}
                />
              </Box>
              <Box
                style={{
                  display: "grid",
                  gap: 4,
                  gridTemplateColumns: "46px 46px 46px 46px",
                  marginRight: 8,
                  width: "auto",
                }}
              >
                <BlockButton
                  disabled={disabled}
                  format="block-quote"
                  icon={(p: any) => <FormatQuote {...p} />}
                />
                <BlockButton
                  disabled={disabled}
                  format="numbered-list"
                  icon={(p: any) => <FormatListNumbered {...p} />}
                />
                <BlockButton
                  disabled={disabled}
                  format="bulleted-list"
                  icon={(p: any) => <FormatListBulleted {...p} />}
                />
                <BlockButton
                  disabled={disabled}
                  format="check-list-item"
                  icon={(p: any) => <CheckCircle {...p} />}
                />
              </Box>
            </>
          )}
          {!!extraButtons?.length && (
            <Box
              className="texteditor-extra-actions"
              style={{
                marginRight: 8,
                width: "auto",
              }}
            >
              {extraButtons.map((extraButton, i) => (
                <DefaultButton
                  key={i}
                  type="button"
                  disabled={disabled}
                  aria-label={extraButton.title || t("Text action")}
                  title={extraButton.title || t("Text action")}
                  aria-pressed={extraButton.active}
                  onClick={extraButton.onClick}
                  variant={extraButton.active ? "outlined" : "default"}
                >
                  {extraButton.icon({ size: 16, color: "inherited" })}{" "}
                </DefaultButton>
              ))}
            </Box>
          )}
        </Box>
        <Divider />
        <Box className="texteditor-inner">
          <Editable
            readOnly={disabled}
            role="textbox"
            aria-multiline="true"
            aria-readonly={disabled}
            aria-label={
              ariaLabelledby ? undefined : ariaLabel || t("Rich text")
            }
            aria-labelledby={ariaLabelledby}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder={placeholder}
            spellCheck
            autoFocus={autoFocus}
            onKeyDown={(event) => {
              if (disabled) return;
              for (const hotkey in HOTKEYS) {
                if (isHotkey(hotkey, event as any)) {
                  event.preventDefault();
                  const mark = HOTKEYS[hotkey];
                  toggleMark(editor, mark);
                }
              }
            }}
            onBlur={() => {
              if (timeout.current) clearTimeout(timeout.current);
              blurHandler.current?.(editor.children);
            }}
            onClick={onClick}
          />
        </Box>
      </Slate>
    </Box>
  );
};

const toggleBlock = (editor: BaseEditor, format: string) => {
  try {
    const isActive = isBlockActive(editor, format);
    const isList = LIST_TYPES.includes(format);

    const options: any = {
      match: (n: Node & { type: string }) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        LIST_TYPES.includes(n.type),
      split: true,
    };

    Transforms.unwrapNodes(editor, options);

    const newProperties: any = {
      type: isActive ? "paragraph" : isList ? "list-item" : format,
    };
    Transforms.setNodes(editor, newProperties);

    if (!isActive && isList) {
      const block = { type: format, children: [] };
      Transforms.wrapNodes(editor, block);
    }
  } catch (error) {
    console.error("TextEditor toggleBlock failed", error);
  }
};

const toggleMark = (editor: BaseEditor, format: string) => {
  try {
    const isActive = isMarkActive(editor, format);

    if (isActive) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, true);
    }
  } catch (error) {
    console.error("TextEditor toggleMark failed", error);
  }
};

const isBlockActive = (editor: BaseEditor, format: string) => {
  try {
    const { selection } = editor;
    if (!selection) return false;

    const options: any = {
      at: Editor.unhangRange(editor, selection),
      match: (n: Node & { type: string }) =>
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
    };

    const [match] = Editor.nodes(editor, options);

    return !!match;
  } catch (error) {
    console.error("TextEditor isBlockActive failed", error);
  }

  return false;
};

const isMarkActive = (editor: BaseEditor, format: any) => {
  try {
    const marks: any = Editor.marks(editor);
    return marks ? marks[format] === true : false;
  } catch (error) {
    console.error("TextEditor isMarkActive failed", error);
  }

  return false;
};

const Element: React.FC<any> = ({ attributes, children, element }) => {
  switch (element?.type) {
    case "block-quote":
      return <blockquote {...attributes}>{children}</blockquote>;
    case "bulleted-list":
      return <ul {...attributes}>{children}</ul>;
    case "heading-one":
      return <h1 {...attributes}>{children}</h1>;
    case "heading-two":
      return <h2 {...attributes}>{children}</h2>;
    case "list-item":
      return <li {...attributes}>{children}</li>;
    case "numbered-list":
      return <ol {...attributes}>{children}</ol>;
    case "check-list-item":
      return (
        <CheckListItemElement {...{ attributes, element }}>
          {children}
        </CheckListItemElement>
      );
    default:
      return <p {...attributes}>{children}</p>;
  }
};

const Leaf: React.FC<any> = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.code) {
    children = <code>{children}</code>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  return <span {...attributes}>{children}</span>;
};

const FORMAT_LABELS: Record<string, string> = {
  bold: "Bold",
  italic: "Italic",
  underline: "Underline",
  "heading-one": "Heading 1",
  "heading-two": "Heading 2",
  "block-quote": "Quote",
  "numbered-list": "Numbered list",
  "bulleted-list": "Bulleted list",
  "check-list-item": "Checklist",
};
const BlockButton: React.FC<any> = ({ format, icon, disabled }) => {
  const editor = useSlate();
  const { t } = useTranslation("common");
  return (
    <Button
      type="button"
      disabled={disabled}
      active={isBlockActive(editor, format)}
      aria-label={t(FORMAT_LABELS[format] || format)}
      title={t(FORMAT_LABELS[format] || format)}
      aria-pressed={isBlockActive(editor, format)}
      onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
      onClick={() => {
        ReactEditor.focus(editor as ReactEditor);
        toggleBlock(editor, format);
      }}
      icon={icon}
    />
  );
};
const MarkButton: React.FC<any> = ({ format, icon, disabled }) => {
  const editor = useSlate();
  const { t } = useTranslation("common");
  return (
    <Button
      type="button"
      disabled={disabled}
      active={isMarkActive(editor, format)}
      aria-label={t(FORMAT_LABELS[format] || format)}
      title={t(FORMAT_LABELS[format] || format)}
      aria-pressed={isMarkActive(editor, format)}
      onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
      onClick={() => {
        ReactEditor.focus(editor as ReactEditor);
        toggleMark(editor, format);
      }}
      icon={icon}
    />
  );
};

const CheckListItemElement: React.FC<any> = ({
  attributes,
  children,
  element,
}) => {
  const editor = withReact(useSlateStatic() as ReactEditor);
  const { t } = useTranslation("common");
  const readOnly = useReadOnly();
  const { checked } = element;
  return (
    <div {...attributes} className="slateCheckbox">
      <span contentEditable={false} className="first-span">
        <Checkbox
          checked={checked}
          disabled={readOnly}
          aria-label={
            element.children?.map((child: any) => child.text || "").join("") ||
            t("Checklist")
          }
          onChange={(event) => {
            try {
              const path = ReactEditor.findPath(editor, element);
              const newProperties: any = {
                checked: event.target.checked,
              };
              Transforms.setNodes(editor, newProperties, { at: path });
            } catch (error) {
              console.error("TextEditor checkbox toggle failed", error);
            }
          }}
        />
      </span>
      <span
        contentEditable={!readOnly}
        suppressContentEditableWarning
        className="second-span"
        style={{
          opacity: checked ? 0.666 : 1,
          textDecoration: !checked ? "none" : "line-through",
        }}
      >
        {children}
      </span>
    </div>
  );
};
export default TextEditor;
